import React from 'react';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RadarIcon from '@mui/icons-material/Radar';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { QUICK_FILTERS, QUICK_FILTER_LABELS } from '../constants/quickFilters';
import SaveAsDialog from '../components/SaveAsDialog';
import FilterListItem from './components/FilterListItem';

export { QUICK_FILTERS, QUICK_FILTER_LABELS };

const SELECTED_SX = {
    '&.Mui-selected': {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { bgcolor: 'primary.dark' },
    },
};

// "My Products" — author-scoped quick filters. Icons are shown in the collapsed rail.
const MY_PRODUCT_ITEMS = [
    { key: QUICK_FILTERS.MY_DRAFTS, icon: <EditNoteIcon fontSize="small" /> },
    { key: QUICK_FILTERS.MY_UNDER_REVIEW, icon: <RateReviewOutlinedIcon fontSize="small" /> },
];

// "All Products" — org-wide categories (no author filter), mirroring the original FINTEL /
// Disclosures sidebar entries.
const ALL_PRODUCT_ITEMS = [
    { key: QUICK_FILTERS.ALL_DRAFTS, icon: <DraftsOutlinedIcon fontSize="small" /> },
    { key: QUICK_FILTERS.PITCHES, icon: <LightbulbOutlinedIcon fontSize="small" /> },
    { key: QUICK_FILTERS.FINTEL, icon: <AssessmentIcon fontSize="small" /> },
    { key: QUICK_FILTERS.SITREP, icon: <RadarIcon fontSize="small" /> },
    { key: QUICK_FILTERS.DISCLOSURES, icon: <ShareOutlinedIcon fontSize="small" /> },
];

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 52;

// Viewport width below which the panel auto-collapses when the user has no explicit preference.
const AUTO_COLLAPSE_BREAKPOINT = 1024;
const getAutoCollapsed = () => typeof window !== 'undefined' && window.innerWidth < AUTO_COLLAPSE_BREAKPOINT;

/**
 * Left navigation panel for the All Products page.
 *
 * Props:
 *   activeQuickFilter       - current QUICK_FILTERS value or 'saved:<id>'
 *   onQuickFilter(key)      - called when user clicks a quick-filter nav item
 *   savedFilters            - SavedFilter[] from useSavedFilters, ordered by .order
 *   onApplySavedFilter(sf)  - navigate URL to the saved filter
 *   onNewFilter()           - opens the Save As dialog in the parent
 *   onRenameFilter(id,name)
 *   onDeleteSavedFilter(id)
 *   onReorderFilters(newIds) - called with new ordered id array after drag
 *   maxFilterNameLength      - character limit for filter names
 */
const ProductsLeftPanel = ({
    activeQuickFilter,
    onQuickFilter,
    savedFilters,
    maxFilterNameLength,
    onApplySavedFilter,
    onNewFilter,
    onRenameFilter,
    onDeleteSavedFilter,
    onReorderFilters,
}) => {
    const [activeId, setActiveId] = React.useState(null);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [menuFilterId, setMenuFilterId] = React.useState(null);
    const [renameTarget, setRenameTarget] = React.useState(null);  // { id, name }
    const [deleteTarget, setDeleteTarget] = React.useState(null);  // { id, name }

    // Collapse state: null = follow auto (viewport) rule; 'open' | 'closed' = explicit user choice.
    const [collapsePreference, setCollapsePreference] = React.useState(null);
    const [autoCollapsed, setAutoCollapsed] = React.useState(getAutoCollapsed);
    React.useEffect(() => {
        const onResize = () => setAutoCollapsed(getAutoCollapsed());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const collapsed = collapsePreference === null ? autoCollapsed : collapsePreference === 'closed';
    const handleToggleCollapse = React.useCallback(
        () => setCollapsePreference((prev) => {
            const isCollapsed = prev === null ? getAutoCollapsed() : prev === 'closed';
            return isCollapsed ? 'open' : 'closed';
        }),
        [],
    );

    // Sorted by .order asc, then createdAt desc (newest first) as tiebreaker.
    // New filters get order -1 so they appear above existing ordered entries.
    const sortedFilters = React.useMemo(
        () => [...savedFilters].sort(
            (a, b) =>
                (a.order ?? Infinity) - (b.order ?? Infinity) ||
                (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
        ),
        [savedFilters],
    );

    const sortedIds = React.useMemo(() => sortedFilters.map((f) => f.id), [sortedFilters]);

    // Optimistic local id order — updated immediately on drop so the list
    // doesn't jump back while the prop round-trip from useSavedFilters arrives.
    // Frozen during an active drag to prevent mid-drag flicker from external updates.
    const [localIds, setLocalIds] = React.useState(sortedIds);
    React.useEffect(() => {
        if (!activeId) setLocalIds(sortedIds);
    }, [sortedIds, activeId]);

    // Build the display list from localIds (optimistic) so the order is always current.
    const idToFilter = React.useMemo(
        () => new Map(sortedFilters.map((f) => [f.id, f])),
        [sortedFilters],
    );

    const sensors = useSensors(
        // distance: 8 — large enough to distinguish a drag from a click
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    );

    const handleDragEnd = React.useCallback((event) => {
        const { active, over } = event;
        // Clear overlay first, then apply optimistic order — both in the same batch.
        setActiveId(null);
        if (!over || active.id === over.id) return;
        const oldIndex = localIds.indexOf(active.id);
        const newIndex = localIds.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(localIds, oldIndex, newIndex);
        setLocalIds(reordered);         // immediate — no jump
        onReorderFilters(reordered);    // async prop round-trip (fire-and-forget)
    }, [localIds, onReorderFilters]);

    const handleOpenMenu = React.useCallback((e, id) => {
        setMenuAnchor(e.currentTarget);
        setMenuFilterId(id);
    }, []);

    const handleCloseMenu = React.useCallback(() => {
        setMenuAnchor(null);
        setMenuFilterId(null);
    }, []);

    const handleRenameClick = React.useCallback(() => {
        const sf = savedFilters.find((f) => f.id === menuFilterId);
        if (sf) setRenameTarget({ id: sf.id, name: sf.name });
        handleCloseMenu();
    }, [savedFilters, menuFilterId, handleCloseMenu]);

    const handleDeleteClick = React.useCallback(() => {
        const sf = savedFilters.find((f) => f.id === menuFilterId);
        if (sf) setDeleteTarget({ id: sf.id, name: sf.name });
        handleCloseMenu();
    }, [savedFilters, menuFilterId, handleCloseMenu]);

    const handleDeleteConfirm = React.useCallback(() => {
        if (deleteTarget) onDeleteSavedFilter(deleteTarget.id);
        setDeleteTarget(null);
    }, [deleteTarget, onDeleteSavedFilter]);

    /** A quick-filter row — icon always rendered; label hidden while collapsed. */
    const renderQuickItem = (key, icon, { id } = {}) => {
        const button = (
            <ListItemButton
                key={key}
                id={id}
                selected={activeQuickFilter === key}
                onClick={() => onQuickFilter(key)}
                sx={{
                    ...SELECTED_SX,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1.5 : 2,
                }}
            >
                <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1.5, color: 'inherit', justifyContent: 'center' }}>
                    {icon}
                </ListItemIcon>
                {!collapsed && (
                    <ListItemText
                        primary={QUICK_FILTER_LABELS[key] ?? key}
                        primaryTypographyProps={{ variant: 'body2' }}
                    />
                )}
            </ListItemButton>
        );
        return collapsed
            ? <Tooltip key={key} title={QUICK_FILTER_LABELS[key] ?? key} placement="right" arrow>{button}</Tooltip>
            : button;
    };

    return (
        <Stack
            py={1}
            sx={{
                width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
                minWidth: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                transition: 'width 0.2s ease, min-width 0.2s ease',
            }}
        >
            {/* Collapsed: standalone centered expand toggle */}
            {collapsed && (
                <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                    <Tooltip title="Expand" placement="right" arrow>
                        <IconButton size="small" onClick={handleToggleCollapse} sx={{ color: 'text.secondary' }}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            <List dense disablePadding>
                {/* My Products header — doubles as the collapse toggle row when expanded */}
                {!collapsed && (
                    <ListSubheader
                        disableSticky
                        sx={{ lineHeight: '32px', pl: 2, pr: 0.5, display: 'flex', alignItems: 'center' }}
                    >
                        <Typography component="span" variant="inherit" sx={{ flexGrow: 1 }}>
                            My Products
                        </Typography>
                        <Tooltip title="Collapse" placement="right" arrow>
                            <IconButton size="small" onClick={handleToggleCollapse} sx={{ color: 'text.secondary' }}>
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ListSubheader>
                )}

                {MY_PRODUCT_ITEMS.map(({ key, icon }) => renderQuickItem(key, icon))}

                <Divider sx={{ my: 0.5 }} />

                {/* All Products — org-wide categories (no author filter) */}
                {!collapsed && (
                    <ListSubheader disableSticky sx={{ lineHeight: '32px', px: 2 }}>
                        All Products
                    </ListSubheader>
                )}

                {ALL_PRODUCT_ITEMS.map(({ key, icon }) =>
                    renderQuickItem(key, icon, key === QUICK_FILTERS.ALL_DRAFTS ? { id: 'quick-filter-all-drafts' } : undefined),
                )}

                {/* Saved Filters — hidden while collapsed */}
                {!collapsed && <Divider sx={{ my: 0.5 }} />}

                {!collapsed && (
                    <ListSubheader
                        disableSticky
                        sx={{ lineHeight: '32px', pl: 2, pr: 1, display: 'flex', alignItems: 'center' }}
                    >
                        <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Saved Filters
                        </Typography>
                        <Tooltip title="New saved filter">
                            <IconButton size="small" onClick={onNewFilter}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ListSubheader>
                )}

                {/* Drag-to-reorder list */}
                {collapsed ? null : localIds.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={({ active }) => setActiveId(active.id)}
                        onDragEnd={handleDragEnd}
                        onDragCancel={() => setActiveId(null)}
                    >
                        <SortableContext items={localIds} strategy={verticalListSortingStrategy}>
                            {localIds.map((id) => {
                                const sf = idToFilter.get(id);
                                return sf ? (
                                    <FilterListItem
                                        key={sf.id}
                                        sf={sf}
                                        isActive={activeQuickFilter === `saved:${sf.id}`}
                                        onApply={onApplySavedFilter}
                                        onOpenMenu={handleOpenMenu}
                                    />
                                ) : null;
                            })}
                        </SortableContext>
                        <DragOverlay dropAnimation={null}>
                            {activeId ? (() => {
                                const sf = idToFilter.get(activeId);
                                return sf ? (
                                    <ListItemButton
                                        sx={{
                                            pr: 5,
                                            boxShadow: 4,
                                            borderRadius: 1,
                                            bgcolor: 'background.paper',
                                            cursor: 'grabbing',
                                            opacity: 0.95,
                                        }}
                                    >
                                        <ListItemText
                                            primary={sf.name}
                                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                        />
                                    </ListItemButton>
                                ) : null;
                            })() : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 1 }}>
                        No saved filters
                    </Typography>
                )}
            </List>

            {/* Handle-icon context menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MenuItem onClick={handleRenameClick}>Rename</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>Delete</MenuItem>
            </Menu>

            {/* Rename dialog */}
            <SaveAsDialog
                open={Boolean(renameTarget)}
                title="Rename filter"
                initialName={renameTarget?.name ?? ''}
                maxLength={maxFilterNameLength}
                onClose={() => setRenameTarget(null)}
                onConfirm={(name) => {
                    onRenameFilter(renameTarget.id, name);
                    setRenameTarget(null);
                }}
            />

            {/* Delete confirmation dialog */}
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete filter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ProductsLeftPanel;
